// ClaimsBatchRegistration.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { format } from 'date-fns';
import { BASE_URL } from '../../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSpinner,
  faPlus,
  faEdit,
  faEye, // Added for View button
  faPaperclip,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ClaimsBatchRegistration = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [batchLookupTerm, setBatchLookupTerm] = useState('');
  const [batchLookupResults, setBatchLookupResults] = useState([]);
  const [batchLookupLoading, setBatchLookupLoading] = useState(false);
  const [batchLookupError, setBatchLookupError] = useState('');
  const [batchLookupSearched, setBatchLookupSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [filters, setFilters] = useState({
    batch_no: '',
    provider_name: '',
    received_date: '',
    due_date: '',
    batch_status: '',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'created_date',
    direction: 'desc',
  });
  const [soaModalOpen, setSoaModalOpen] = useState(false);
  const [soaModalBatch, setSoaModalBatch] = useState(null);
  const [soaModalFiles, setSoaModalFiles] = useState([]);
  const [soaUploading, setSoaUploading] = useState(false);
  const [soaModalLoading, setSoaModalLoading] = useState(false);

  const allowedFileTypes = ['pdf', 'jpg', 'jpeg', 'png'];
  const maxFileSizeMB = 10;
  const maxUploadLimit = 10;

  const statusMap = {
    Open: { name: 'Open', color: 'bg-emerald-100 text-emerald-900' }, // Richer green
    Close: { name: 'Close', color: 'bg-rose-100 text-rose-900' }, // Softer red
    Draft: { name: 'Draft', color: 'bg-amber-100 text-amber-900' }, // Warm amber for neutral
  };

  const columns = [
    { key: 'batch_no', label: 'Batch No.', sortable: true },
    { key: 'received_date', label: 'Received Date', sortable: true },
    { key: 'due_date', label: 'Due Date', sortable: true },
    { key: 'provider_reference_no', label: 'Provider Ref. No.', sortable: false },
    { key: 'soa_amount', label: 'SOA Amount', sortable: true },
    { key: 'provider_name', label: 'Provider Name', sortable: true },
    { key: 'batch_type', label: 'Batch Type', sortable: false },
    { key: 'batch_status', label: 'Status', sortable: true },
    { key: 'act_claim_count', label: 'Actual Claim Count', sortable: true },
    { key: 'act_claim_amount', label: 'Actual Claim Amount', sortable: true },
    { key: 'created_date', label: 'Created At', sortable: true },
    { key: 'submitted_date', label: 'Submitted At', sortable: true },
    { key: 'remarks', label: 'Remarks', sortable: false },
    { key: 'action', label: 'Action', sortable: false },
  ];

  const phpFormatter = new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const isValidDateValue = (value) => {
    if (!value) return false;
    if (typeof value === 'string' && value.startsWith('0000-00-00')) return false;
    const date = new Date(value);
    return !Number.isNaN(date.getTime());
  };

  const formatSafeDate = (value, dateFormat) => {
    if (!isValidDateValue(value)) return '-';
    try {
      return format(new Date(value), dateFormat);
    } catch (error) {
      return '-';
    }
  };

  const normalizeSoaFiles = (rawFiles) => {
    if (!Array.isArray(rawFiles)) return [];
    return rawFiles.map((file, index) => ({
      uploadId: file.uploadId || file.upload_id || file.uploadID || null,
      signed_url: file.signed_url || file.file_url || file.fileUrl || null,
      file_name: file.file_name || file.name || file.original_file_name || `SOA-${index + 1}`,
      original_file_name: file.original_file_name || file.file_name || file.name || `SOA-${index + 1}`,
      file_size: file.file_size || file.size || null,
    }));
  };

  const openSoaModal = (batch) => {
    let parsed = [];
    try {
      parsed = JSON.parse(batch?.soa_fileupload || '[]');
    } catch (error) {
      parsed = [];
    }
    setSoaModalBatch(batch);
    setSoaModalFiles(normalizeSoaFiles(parsed));
    setSoaModalOpen(true);
    if (batch?.batch_no) {
      fetchSoaFilesForBatch(batch.batch_no);
    }
  };

  const closeSoaModal = () => {
    setSoaModalOpen(false);
    setSoaModalBatch(null);
    setSoaModalFiles([]);
  };

  const fetchSoaFilesForBatch = async (batchNo) => {
    setSoaModalLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/claims/batches/${batchNo}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      const batch = response.data?.data || {};
      let parsed = [];
      if (Array.isArray(batch.soa_fileupload)) {
        parsed = batch.soa_fileupload;
      } else {
        try {
          parsed = JSON.parse(batch.soa_fileupload || '[]');
        } catch (error) {
          parsed = [];
        }
      }
      const normalized = normalizeSoaFiles(parsed);
      setSoaModalFiles(normalized);
      updateBatchSoaFiles(batchNo, normalized);
    } catch (error) {
      toast.error('Failed to fetch SOA files for this batch.', { duration: 4000 });
    } finally {
      setSoaModalLoading(false);
    }
  };

  const updateBatchSoaFiles = (batchNo, files) => {
    const normalized = normalizeSoaFiles(files);
    setBatches((prev) =>
      prev.map((batch) =>
        batch.batch_no === batchNo
          ? {
              ...batch,
              soa_fileupload: JSON.stringify(
                normalized.map((f) => ({
                  uploadId: f.uploadId,
                  signed_url: f.signed_url,
                  file_name: f.file_name,
                }))
              ),
            }
          : batch
      )
    );
  };

  const handleRefreshSoaLink = async (fileObj) => {
    if (!fileObj?.uploadId) {
      toast.error('Missing upload ID for refresh.', { duration: 4000 });
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/claims/refresh-url`,
        { upload_id: fileObj.uploadId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      const refreshedUrl =
        response.data?.signed_url ||
        response.data?.file_url ||
        response.data?.file?.signed_url;

      if (!refreshedUrl) {
        toast.error('Failed to refresh file link.', { duration: 4000 });
        return;
      }

      setSoaModalFiles((prev) => {
        const updated = prev.map((item) =>
          item.uploadId === fileObj.uploadId ? { ...item, signed_url: refreshedUrl } : item
        );
        if (soaModalBatch?.batch_no) {
          updateBatchSoaFiles(soaModalBatch.batch_no, updated);
        }
        return updated;
      });

      toast.success('Link refreshed successfully.', { duration: 3000 });
    } catch (error) {
      toast.error('Failed to refresh file link.', { duration: 4000 });
    }
  };

  const applyAckStyles = () => {
    const popup = Swal.getPopup();
    if (!popup) return;
    let style = popup.querySelector('#ack-checkbox-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'ack-checkbox-style';
      style.textContent = `
        .ack-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        .ack-checkbox input {
          transform: scale(1.2);
          margin-right: 6px;
        }
        .ack-checkbox.ack-pulse {
          background: #fff7ed;
          border-color: #fb923c;
          box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.35);
          animation: ackPulse 1s ease-in-out 2;
        }
        @keyframes ackPulse {
          0% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0.55); }
          70% { box-shadow: 0 0 0 6px rgba(251, 146, 60, 0); }
          100% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0); }
        }
      `;
      popup.appendChild(style);
    }
    const checkbox = popup.querySelector('.swal2-checkbox');
    if (checkbox) {
      checkbox.classList.add('ack-checkbox');
    }
  };

  const pulseAck = () => {
    applyAckStyles();
    const popup = Swal.getPopup();
    const checkbox = popup?.querySelector('.swal2-checkbox');
    if (checkbox) {
      checkbox.classList.remove('ack-pulse');
      void checkbox.offsetWidth;
      checkbox.classList.add('ack-pulse');
    }
  };

  const handleRemoveSoaFile = async (fileObj) => {
    if (!fileObj?.uploadId) {
      toast.error('Missing upload ID for removal.', { duration: 4000 });
      return;
    }

    const confirmResult = await MySwal.fire({
      title: 'Remove SOA File?',
      text: `Are you sure you want to remove ${fileObj.original_file_name || fileObj.file_name || 'this file'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Remove',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        actions: 'gap-3',
        confirmButton: 'rounded-lg px-5 py-2.5 font-semibold',
        cancelButton: 'rounded-lg px-5 py-2.5 font-semibold',
      },
    });

    if (!confirmResult.isConfirmed) return;

    const ackResult = await MySwal.fire({
      title: 'Acknowledgment',
      icon: 'info',
      input: 'checkbox',
      inputValue: 0,
      inputPlaceholder:
        'I acknowledge that removing SOA files affects batch completeness and may be subject to review.',
      showCancelButton: true,
      confirmButtonText: 'I Accept Responsibility',
      cancelButtonText: 'Go Back',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        actions: 'gap-3',
        confirmButton: 'rounded-lg px-5 py-2.5 font-semibold',
        cancelButton: 'rounded-lg px-5 py-2.5 font-semibold',
      },
      inputValidator: (value) => {
        if (value) return undefined;
        pulseAck();
        return `Please acknowledge before proceeding.<div style="font-size:12px;color:#6b7280;margin-top:4px;">You need to click the checkbox on the acknowledgment message before proceeding.</div>`;
      },
      didOpen: () => {
        applyAckStyles();
      },
    });

    if (!ackResult.isConfirmed) return;

    setSoaUploading(true);
    try {
      await axios.delete(
        `${BASE_URL}/claims/batch-header/upload-soa/${fileObj.uploadId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
          },
        }
      );

      setSoaModalFiles((prev) => {
        const updated = prev.filter((item) => item.uploadId !== fileObj.uploadId);
        if (soaModalBatch?.batch_no) {
          updateBatchSoaFiles(soaModalBatch.batch_no, updated);
        }
        return updated;
      });

      toast.success('SOA file removed successfully.', { duration: 3000 });
    } catch (error) {
      toast.error('Failed to remove SOA file.', { duration: 4000 });
    } finally {
      setSoaUploading(false);
    }
  };

  const uploadSoaFile = async (file, batchNo) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('batch_no', batchNo);

    const response = await axios.post(`${BASE_URL}/claims/batch-header/upload-soa`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    const uploadedFile = response.data?.files?.[0] || {};
    const uploadId = uploadedFile.upload_id || response.data?.upload_id;
    const signed_url = uploadedFile.signed_url || response.data?.signed_url;
    const file_name = uploadedFile.file_name || response.data?.file_name || file.name;
    const original_file_name = uploadedFile.original_file_name || file.name;
    const file_size = uploadedFile.file_size || file.size || null;

    return {
      uploadId,
      signed_url,
      file_name,
      original_file_name,
      file_size,
    };
  };

  const handleSoaFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!soaModalBatch?.batch_no || files.length === 0) return;

    const validFiles = files.filter((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(', ')} files allowed.`,
          { duration: 4000 }
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(`File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`, {
          duration: 4000,
        });
        return false;
      }

      return true;
    });

    if (soaModalFiles.length + validFiles.length > maxUploadLimit) {
      toast.error(`You can only upload up to ${maxUploadLimit} files at a time.`, {
        duration: 4000,
      });
      return;
    }

    setSoaUploading(true);
    try {
      const uploadedFiles = [];
      for (const file of validFiles) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadSoaFile(file, soaModalBatch.batch_no);
        if (uploaded?.uploadId) {
          uploadedFiles.push(uploaded);
        }
      }

      if (uploadedFiles.length > 0) {
        setSoaModalFiles((prev) => {
          const merged = [...prev, ...uploadedFiles];
          updateBatchSoaFiles(soaModalBatch.batch_no, merged);
          return merged;
        });
        toast.success('SOA file(s) uploaded successfully.', { duration: 3000 });
      }
    } catch (error) {
      toast.error('Failed to upload SOA file(s).', { duration: 4000 });
    } finally {
      setSoaUploading(false);
      event.target.value = '';
    }
  };

  useEffect(() => {
    fetchBatches();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/claims/batches`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setBatches(response.data.data.data);
      setTotalPages(response.data.data.last_page);
      setTotalEntries(response.data.data.total);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this.', { duration: 4000 });
      }
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again later.', { duration: 4000 });
      }
      toast.error('Failed to fetch batch headers.', { duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const applyBatchSearch = (batchNo) => {
    if (!batchNo) return;
    setSearchTerm(batchNo);
    setCurrentPage(1);
    toast.success(`Batch ${batchNo} applied to search.`, { duration: 3000 });
  };

  const handleBatchLookup = async (event) => {
    if (event) event.preventDefault();
    const term = batchLookupTerm.trim();
    if (!term) {
      toast.error('Please enter a LOA or Transaction number.', { duration: 4000 });
      return;
    }

    setBatchLookupLoading(true);
    setBatchLookupError('');
    setBatchLookupSearched(true);
    setBatchLookupResults([]);

    try {
      const response = await axios.get(`${BASE_URL}/claims/batches/lookup`, {
        params: {
          search: term,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });

      const results = response.data?.data || [];
      setBatchLookupResults(results);

      if (results.length === 1 && results[0]?.batch_no) {
        applyBatchSearch(results[0].batch_no);
      }
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access this.', { duration: 4000 });
      }
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again later.', { duration: 4000 });
      }

      const errorMessage =
        typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : 'Failed to search batch details.';
      setBatchLookupError(errorMessage);
      toast.error('Failed to search batch details.', { duration: 4000 });
    } finally {
      setBatchLookupLoading(false);
    }
  };

  const handleBatchLookupClear = () => {
    setBatchLookupTerm('');
    setBatchLookupResults([]);
    setBatchLookupError('');
    setBatchLookupSearched(false);
  };

  const handleBatchLookupKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleBatchLookup(event);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2) startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-indigo-100'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} entries`;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 15 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon icon={faSyncAlt} className="text-indigo-600 text-lg" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Claims Batch Registration & Received/Scanned Date Uploading</h1>
            <p className="text-sm text-gray-500">View and manage your claims batch headers</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Batch Lookup */}
          <div className="p-6 border-b border-gray-100 bg-slate-50">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Search Batch by LOA / Transaction No.
                </label>
                <div className="relative mt-2">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Enter LOA# or Transaction No."
                    value={batchLookupTerm}
                    onChange={(e) => setBatchLookupTerm(e.target.value)}
                    onKeyDown={handleBatchLookupKeyDown}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    aria-label="Search batch by LOA or transaction"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Once a batch number is found, it will fill the main batch search field below.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBatchLookup}
                  disabled={batchLookupLoading}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-sm disabled:opacity-60"
                >
                  <FontAwesomeIcon
                    icon={batchLookupLoading ? faSpinner : faSearch}
                    spin={batchLookupLoading}
                  />
                  {batchLookupLoading ? 'Searching' : 'Search'}
                </button>
                <button
                  onClick={handleBatchLookupClear}
                  disabled={!batchLookupTerm && batchLookupResults.length === 0 && !batchLookupError}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-all duration-200 disabled:opacity-60"
                >
                  Clear
                </button>
              </div>
            </div>

            {batchLookupError && (
              <div className="mt-3 text-sm text-red-600">{batchLookupError}</div>
            )}

            {batchLookupSearched &&
              !batchLookupLoading &&
              !batchLookupError &&
              batchLookupResults.length === 0 && (
                <div className="mt-3 text-sm text-gray-500">
                  No matching records found.
                </div>
              )}

            {batchLookupResults.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white">
                    <tr className="text-left text-xs uppercase text-gray-500 tracking-wider">
                      <th className="p-2">Batch No.</th>
                      <th className="p-2">Trans No.</th>
                      <th className="p-2">LOA No.</th>
                      <th className="p-2">Provider</th>
                      <th className="p-2">Received</th>
                      <th className="p-2">Scanned</th>
                      <th className="p-2">Uploaded By</th>
                      <th className="p-2">Remarks</th>
                      <th className="p-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {batchLookupResults.map((row, index) => (
                      <tr
                        key={`${row.trans_no || 'row'}-${index}`}
                        className="bg-white"
                      >
                        <td className="p-2 font-medium text-gray-900">
                          {row.batch_no || '-'}
                        </td>
                        <td className="p-2">{row.trans_no || '-'}</td>
                        <td className="p-2">{row.loa_no || '-'}</td>
                        <td className="p-2">
                          {row.provider_name || row.provider || '-'}
                        </td>
                        <td className="p-2">
                          {formatSafeDate(row.date_received, 'yyyy-MM-dd')}
                        </td>
                        <td className="p-2">
                          {formatSafeDate(row.date_scanned, 'yyyy-MM-dd')}
                        </td>
                        <td className="p-2">{row.uploaded_by || '-'}</td>
                        <td className="p-2">{row.remarks || '-'}</td>
                        <td className="p-2">
                          {row.batch_no ? (
                            <button
                              onClick={() => applyBatchSearch(row.batch_no)}
                              className="text-indigo-600 hover:text-indigo-800 text-xs font-semibold"
                            >
                              Use Batch#
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">No batch</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* Top Controls */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search batches..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  aria-label="Search batches"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={fetchBatches}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-sm"
                  data-tooltip-id="refresh-tooltip"
                  data-tooltip-content="Refresh batch list"
                >
                  <FontAwesomeIcon icon={faSyncAlt} />
                  Refresh
                </button>
                <button
                  onClick={() => navigate('/claims/batches/add')}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-green-600 rounded-xl hover:bg-green-700 transition-all duration-200 shadow-sm"
                  data-tooltip-id="add-tooltip"
                  data-tooltip-content="Add new batch"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Batch
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            <div className="flex flex-wrap gap-4 min-w-max">
              {[
                { key: 'batch_no', label: 'Batch No.' },
                { key: 'provider_name', label: 'Provider Name' },
                { key: 'received_date', label: 'Received Date', type: 'date' },
                { key: 'due_date', label: 'Due Date', type: 'date' },
                { key: 'batch_status', label: 'Status', type: 'select' },
              ].map((filter) => (
                <div key={filter.key} className="flex-1 min-w-[150px] flex flex-col gap-1">
                  <label
                    htmlFor={filter.key}
                    className="text-sm font-medium text-gray-700"
                  >
                    Filter by {filter.label}
                  </label>
                  {filter.type === 'select' ? (
                    <select
                      id={filter.key}
                      value={filters[filter.key]}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      aria-label={`Filter by ${filter.label}`}
                    >
                      <option value="">All Statuses</option>
                      {Object.entries(statusMap).map(([key, { name }]) => (
                        <option key={key} value={key}>
                          {name}
                        </option>
                      ))}
                    </select>
                  ) : filter.type === 'date' ? (
                    <input
                      id={filter.key}
                      type="date"
                      value={filters[filter.key]}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      aria-label={`Filter by ${filter.label}`}
                    />
                  ) : (
                    <input
                      id={filter.key}
                      type="text"
                      placeholder={`Filter by ${filter.label}`}
                      value={filters[filter.key]}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      aria-label={`Filter by ${filter.label}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                      }`}
                      onClick={() => column.sortable && handleSort(column.key)}
                      aria-sort={
                        sortConfig.key === column.key
                          ? sortConfig.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.sortable && (
                          <div className="flex flex-col text-xs">
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === 'asc'
                                  ? 'text-indigo-600'
                                  : 'text-gray-400'
                              }`}
                            >
                              ▲
                            </span>
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === 'desc'
                                  ? 'text-indigo-600'
                                  : 'text-gray-400'
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                ) : batches.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="p-3 text-center text-gray-500 text-sm">
                      No batches found.
                    </td>
                  </tr>
                ) : (
                  batches.map((batch) => (
                    <tr key={batch.tbl_id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm">{batch.batch_no || '-'}</td>
                      <td className="p-3 text-sm">
                        {formatSafeDate(batch.received_date, 'yyyy-MM-dd')}
                      </td>
                      <td className="p-3 text-sm">
                        {formatSafeDate(batch.due_date, 'yyyy-MM-dd')}
                      </td>
                      <td className="p-3 text-sm">{batch.provider_reference_no || '-'}</td>
                      <td className="p-3 text-sm">
                        {batch.soa_amount != null ? phpFormatter.format(batch.soa_amount) : '-'}
                      </td>
                      <td className="p-3 text-sm">{batch.provider_name || '-'}</td>
                      <td className="p-3 text-sm">{batch.batch_type || '-'}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            statusMap[batch.batch_status]?.color || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {statusMap[batch.batch_status]?.name || batch.batch_status || 'Unknown'}
                        </span>
                      </td>
                      <td className="p-3 text-sm">{batch.act_claim_count || '-'}</td>
                      <td className="p-3 text-sm">
                        {batch.act_claim_amount != null
                          ? phpFormatter.format(batch.act_claim_amount)
                          : '-'}
                      </td>
                      <td className="p-3 text-sm">
                        {formatSafeDate(batch.created_date, 'yyyy-MM-dd HH:mm:ss')}
                      </td>
                      <td className="p-3 text-sm">
                        {formatSafeDate(batch.submitted_date, 'yyyy-MM-dd HH:mm:ss')}
                      </td>
                      <td className="p-3 text-sm">{batch.remarks || '-'}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {batch.batch_status === 'Open' || batch.batch_status === 'Draft' ? (
                            <button
                              onClick={() => navigate(`/claims/batches/edit/${batch.batch_no}`)}
                              className="flex items-center gap-2 px-3 py-1 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 whitespace-nowrap"
                              data-tooltip-id={`edit-tooltip-${batch.tbl_id}`}
                              data-tooltip-content="Edit this batch"
                              aria-label={`Edit batch ${batch.batch_no}`}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                              Edit
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate(`/claims/batches/view/${batch.batch_no}`)}
                              className="flex items-center gap-2 px-3 py-1 text-sm text-white bg-gray-600 rounded-lg hover:bg-gray-700 transition-all duration-200 whitespace-nowrap"
                              data-tooltip-id={`view-tooltip-${batch.tbl_id}`}
                              data-tooltip-content="View this batch"
                              aria-label={`View batch ${batch.batch_no}`}
                            >
                              <FontAwesomeIcon icon={faEye} />
                              View
                            </button>
                          )}
                          <button
                            onClick={() => openSoaModal(batch)}
                            className="flex items-center gap-2 px-3 py-1 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all duration-200 whitespace-nowrap"
                            data-tooltip-id={`soa-tooltip-${batch.tbl_id}`}
                            data-tooltip-content="Attach SOA files for this batch"
                            aria-label={`Attach SOA for batch ${batch.batch_no}`}
                          >
                            <FontAwesomeIcon icon={faPaperclip} />
                            Attach SOA Files
                          </button>
                        </div>
                        <Tooltip id={`edit-tooltip-${batch.tbl_id}`} place="top" />
                        <Tooltip id={`view-tooltip-${batch.tbl_id}`} place="top" />
                        <Tooltip id={`soa-tooltip-${batch.tbl_id}`} place="top" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100">
            <div className="flex items-center gap-4 mb-3 md:mb-0">
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                aria-label="Items per page"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>
                    {val} per page
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-600">{getShowingEntriesText()}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="First page"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Last page"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Tooltip id="refresh-tooltip" place="top" />
      <Tooltip id="add-tooltip" place="top" />

      {soaModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Attach SOA - Batch {soaModalBatch?.batch_no || '-'}
                </h2>
                <p className="text-xs text-gray-500">
                  Upload and manage Statement of Account files for this batch.
                </p>
              </div>
              <button
                onClick={closeSoaModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label className="text-sm font-medium text-gray-700">
                  Upload SOA Files
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleSoaFileChange}
                  disabled={soaUploading}
                  className="text-sm"
                  accept={allowedFileTypes.map((ext) => `.${ext}`).join(',')}
                />
                <span className="text-xs text-gray-500">
                  ({allowedFileTypes.join(', ').toUpperCase()}, max {maxFileSizeMB}MB)
                </span>
              </div>
              {soaUploading && (
                <div className="mt-3 text-sm text-indigo-600 flex items-center gap-2">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Uploading SOA file(s)...
                </div>
              )}
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              {soaModalFiles.length === 0 ? (
                soaModalLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Loading SOA files...
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No SOA files attached yet.</p>
                )
              ) : (
                <ul className="space-y-2">
                  {soaModalFiles.map((file) => (
                    <li
                      key={file.uploadId || file.file_name}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900">
                          {file.original_file_name || file.file_name || 'SOA'}
                        </span>
                        {file.file_size ? (
                          <span className="text-xs text-gray-500">
                            {(file.file_size / 1024).toFixed(2)} KB
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        {file.signed_url ? (
                          <a
                            href={file.signed_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400">No link</span>
                        )}
                        <button
                          onClick={() => handleRefreshSoaLink(file)}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          Refresh Link
                        </button>
                        <button
                          onClick={() => handleRemoveSoaFile(file)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsBatchRegistration;
